import React, { useState, useEffect } from 'react';
import { styled } from '@glitz/react';
import { H2, H3, ResponsiveSize } from 'Shared/SharedComponents/atoms/Typography';
import { Plus16x16 } from 'Shared/Icons/Plus';
import SustainabilityViewModel from 'Product/SustainabilityViewModel.type';
import Composition from 'Product/Models/Composition.type';
import DeliveryAndReturnsViewModel from 'Product/DeliveryAndReturnsViewModel.type';
import Html from 'Shared/Partials/PropertyTypes/Html';
import ReviewsContent, { ReviewLabelsPropType } from './ReviewsContent';
import MaterialsContent from './MaterialsContent';
import SustainabilityContent from './SustainabilityContent';
import CareInstructions from 'Product/Models/CareInstructions.type';
import { createGridClasses, createOffsetGridClassLarge } from 'Shared/grid';
import * as style from 'Shared/Style';

type ConnectedPropType = {
  itemNumbers: string[];
  materialsAndCareLabel: string;
  compositionLabel: string;
  compositions: Composition[];
  careLabel: string;
  careInstructions: CareInstructions;
  sustainabilityLabel: string;
  sustainability: SustainabilityViewModel;
  reviewsLabel: string;
  reviewLabels: ReviewLabelsPropType;
  deliveryAndReturnsLabel: string;
  deliveryAndReturns: DeliveryAndReturnsViewModel;
  isCompact: boolean;
  reviewCount: number;
  reviewScore: number;
  expandedSection?: string;
  scrollToSectionRef?: React.Ref<HTMLDivElement>;
  resetExpandedSection?: (newValue: string) => void;
};

type PropType = ConnectedPropType;

const Divider = styled.div({
  height: 0,
  width: '100%',
  marginBottom: '16px',
  border: { y: { width: '1px', style: 'solid', color: style.colors.monochrome.black } },
})

const DividerLine = () => (
  <Divider></Divider>
);

const Wrap = styled.div({
  display: 'flex',
  flexDirection: 'column',
})

const DeliveryHeadingWrap = styled.div({
  marginBottom: '24px',
  ['@media ' + style.mediaMinLarge]: {
    paddingRight: '14px',
  },
})

const Plus16 = styled(Plus16x16, {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'inherit',
  transitionDuration: '0.2s',
})

const SectionInner = styled.div({
  cursor: 'pointer',
  padding: { bottom: '16px' },
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
})

const SectionContent = styled.div({
  padding: {
    y: '24px',
  },

  ['@media ' + style.mediaMinLarge]: {
    padding: {
      y: '64px',
    },
  },
})

function ProductAccordion({
  itemNumbers,
  materialsAndCareLabel,
  compositionLabel,
  compositions,
  careLabel,
  careInstructions,
  sustainabilityLabel,
  sustainability,
  reviewsLabel,
  reviewLabels,
  reviewCount,
  reviewScore,
  deliveryAndReturnsLabel,
  deliveryAndReturns,
  expandedSection,
  scrollToSectionRef,
  resetExpandedSection,
  isCompact,
}: PropType) {

  const [recalculateReviewsHeight, setRecalculateReviewsHeight] = useState<number>(0);

  return (
    <Wrap>
      <DividerLine />
      <AccordionSection key="material" label={materialsAndCareLabel} isCompact={isCompact}>
        <MaterialsContent
          compositionLabel={compositionLabel}
          compositions={compositions}
          careLabel={careLabel}
          careInstructions={careInstructions}
          isCompact={isCompact}
        />
      </AccordionSection>
      <DividerLine />
      {sustainability && (
        <>
          <AccordionSection key="sustainability" label={sustainabilityLabel} isCompact={isCompact}>
            <SustainabilityContent sustainability={sustainability} isCompact={isCompact} />
          </AccordionSection>
          <DividerLine />
        </>
      )}
      {reviewCount > 0 && (
        <>
          <AccordionSection sectionId={'reviews'} resetExpandedSection={() => resetExpandedSection('')} key="reviews" recalculateHeightState={recalculateReviewsHeight} wrapperRef={scrollToSectionRef} expanded={expandedSection && expandedSection === 'reviews'} label={reviewsLabel} isCompact={isCompact}>
            <ReviewsContent reviewsSummary={{ count: reviewCount, score: reviewScore }} recalculateHeight={() => setRecalculateReviewsHeight(recalculateReviewsHeight + 1)} itemNumbers={itemNumbers} reviewLabels={reviewLabels} isCompact={isCompact} />
          </AccordionSection>
          <DividerLine />
        </>
      )}

      <AccordionSection key="delivery-and-returns" label={deliveryAndReturnsLabel} isCompact={isCompact}>
        <div className="grid subgrid-11">
          <DeliveryHeadingWrap className={createGridClasses(12, 5)}>
            <H3>{deliveryAndReturns.heading}</H3>
          </DeliveryHeadingWrap>
          <div className={createGridClasses(12, 6, createOffsetGridClassLarge(6))}>
            {deliveryAndReturns.body && <Html html={deliveryAndReturns.body.html} />}
          </div>
        </div>
      </AccordionSection>
      <DividerLine />
    </Wrap>
  );
}

type SectionPropType = {
  label: string;
  children: React.ReactNode;
  isCompact: boolean;
  expanded?: boolean;
  wrapperRef?: React.Ref<HTMLDivElement>;
  autoHeight?: boolean;
  sectionId?: string;
  recalculateHeightState?: number;
  resetExpandedSection?: () => void;
};

function AccordionSection({ label, children, isCompact, expanded = false, wrapperRef, sectionId, resetExpandedSection }: SectionPropType) {
  const [contentShown, setContentShown] = useState(expanded);
  const [currentHeight, setHeight] = useState(0);
  const [shouldTranslateHeight] = useState(true);

  const updateOffsetHeight = (ref: HTMLElement) => {
    if (ref) {
      requestAnimationFrame(() => {
        const height = ref.offsetHeight;
        if (contentShown && Math.abs(currentHeight - height) > 2) {
          setHeight(height);
        }
      });
    }
  }

  useEffect(() => {
    if (sectionId === 'reviews') {
      if (expanded) {
        setContentShown(true);
        resetExpandedSection();
      }
    }
  }, [expanded]);

  return (
    <div className="grid" ref={wrapperRef}>
      <SectionInner
        className={createGridClasses(12, 12)}
        onClick={() => { setContentShown((isShown) => !isShown) }}
      >
        <H2 fontSize={ResponsiveSize.D32_M24}>{label}</H2>
        <Plus16
          css={{
            transform: contentShown ? 'rotate(45deg)' : undefined,
          }}
        />
      </SectionInner>
      <styled.Div
        className={createGridClasses(12, 11, createOffsetGridClassLarge(2))}
        css={{
          overflow: 'hidden',
          height: contentShown ? `${currentHeight}px` : '0px',
          ...(shouldTranslateHeight && {
            ...style.transition({
              property: 'height',
              duration: `${300}ms`,
              willChange: true,
            }),
          }),
        }}
      >
        <SectionContent ref={ref => updateOffsetHeight(ref)}>
          {children}
        </SectionContent>
      </styled.Div>
    </div>
  );
}

export default ProductAccordion;
